import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as authApi from '@/api/authApi';
import type { SessionResponse, Subscription, User } from '@/api/authApi';

interface AuthContextType {
  user: User | null;
  subscription: Subscription | null;
  requiresPayment: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Refreshes session from server. Returns session data for immediate use. */
  refreshSession: () => Promise<SessionResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Provides authentication state and helpers throughout the app.
 * Checks session on mount and provides refresh mechanism.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async (): Promise<SessionResponse> => {
    try {
      const session = await authApi.getSession();
      setUser(session.user);
      setSubscription(session.subscription);
      setRequiresPayment(session.requires_payment);
      return session;
    } catch {
      // Not authenticated or session expired
      setUser(null);
      setSubscription(null);
      setRequiresPayment(false);
      return { user: null, subscription: null, requires_payment: false };
    }
  }, []);

  useEffect(() => {
    // Get initial session
    refreshSession().finally(() => {
      setLoading(false);
    });
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
      setUser(null);
      setSubscription(null);
      setRequiresPayment(false);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  const value: AuthContextType = {
    user,
    subscription,
    requiresPayment,
    loading,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth user, subscription, and loading state.
 */
export function useSession(): Pick<
  AuthContextType,
  'user' | 'subscription' | 'requiresPayment' | 'loading'
> {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSession must be used within an AuthProvider');
  }
  return {
    user: context.user,
    subscription: context.subscription,
    requiresPayment: context.requiresPayment,
    loading: context.loading,
  };
}

/**
 * Hook to access full auth context including signOut and refreshSession.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
